<template>
  <div
    id="step_variant"
    class="step_separator step-variant p-2.5 flex flex-row gap-4 items-center"
  >
    <a-switch
      v-if="!isTaskType"
      v-model:checked="isActiveLocal"
      :disabled="!canEdit || isUpdating"
    />
    <div
      @click="editVariant"
      v-if="!isMain && !isTaskType"
      class="variant_letter cursor-pointer flex items-center justify-center px-2 py-[6px]"
    >
      {{ variantLetter }}
    </div>
    <div class="flex flex-row gap-2 justify-between w-full">
      <div
        @click="editVariant"
        v-if="hasSubject"
        class="flex flex-row gap-4 items-center cursor-pointer"
      >
        <div class="flex flex-col gap-2 items-center justify-center">
          <span class="text-[15px]">{{
            !variant.reply_previous_thread ? "New Thread" : "Reply"
          }}</span>
        </div>

        <div
          v-if="variant?.subject || computedMessage"
          class="flex flex-col gap-2 justify-center max-w-[45vw]"
        >
          <span class="text-[15px] font-semibold line-clamp-1">{{
            variant.subject || "(No Subject)"
          }}</span>
          
          <SkeletonLoader 
            v-if="isProcessingVariables" 
            :page="loaderPages.INFO_ITEM" 
            class="h-5 w-64"
          />
          <span
            v-else
            class="text-[13px] secondary_color line-clamp-1"
            v-html="decodeHtmlEntities(computedMessage)"
          ></span>
        </div>
      </div>
      <div
        @click="editVariant"
        v-else
        class="flex flex-row gap-4 items-center cursor-pointer"
      >
        <div class="flex flex-col gap-2 justify-center max-w-[45vw]">
          <SkeletonLoader 
            v-if="isProcessingVariables" 
            :page="loaderPages.INFO_ITEM" 
            class="h-5 w-64"
          />
          <span
            v-else
            class="text-[15px] font-semibold line-clamp-1"
            v-html="decodeHtmlEntities(computedMessage)"
          ></span>
        </div>
      </div>
      <div class="flex flex-row items-center">
        <VariantMetrics

          :step="step"
          :metrics="variantMetrics"
          :show-email-metrics="isEmailType"
          v-if="!isTaskType"
        />
        <CustomDropdown
          maxHeight="300px"
          gap="10px"
          position="end"
          v-if="canEdit"
        >
          <template #trigger>
            <div class="quick_action_item mt-1">
              <ThemeImage
                :icon="imageNames.MORE_VERTICAL"
                v-if="canEdit && stepType !== 'PHONE_CALL'"
                class="size-5 cursor-pointer opacity-60"
                alt="three_dots"
              />
            </div>
          </template>

          <template #default>
            <div class="flex flex-col gap-1">
              <div
                v-for="option in filteredVariantActions"
                :key="option.id"
                class="dropdown_item flex items-center flex-row gap-2 px-3 py-2 rounded-lg"
                @click="option.action()"
              >
                <div class="text-sm">{{ option.name }}</div>
              </div>
            </div>
          </template>
        </CustomDropdown>
      </div>
    </div>

    <CustomModal
      :title="`Delete ${featureNames.SEQUENCES} Step`"
      v-model:visible="showDeleteVariantModal"
      :width="'32vw'"
    >
      <span>Are you sure you want to delete this variant?</span>
      <template #footer>
        <a-button class="primary_medium" @click="deleteVariantConfirm"
          >Confirm</a-button
        >
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import SequenceService from "@/src/api/SequenceService.js";
import { message } from "ant-design-vue";
import CustomModal from "@/src/components/CustomModal.vue";
import { sequenceStepTypes } from "../../../constants/sequenceConstants.js";
import { isVariantEmpty } from "@/src/composables/useVariantValidation.js";
import VariantMetrics from "./VariantMetrics.vue";
import { decodeHtmlEntities } from "@/src/utils/utils.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { featureNames } from "@/src/constants/globalConstants";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { extractAiVariableIds, processAiVariableContent } from "@/src/utils/sequenceUtils.js";

const router = useRouter();
const aiVariables = ref([]);
const isProcessingVariables = ref(false);

const props = defineProps({
  step: { type: Object, required: true },
  variant: { type: Object, required: true },
  stepType: { type: String, required: true },
  variantLength: { type: Number, required: true },
  isMain: { type: Boolean, default: false },
  variantLetter: { type: String, default: "A" },
  canEdit: { type: Boolean, required: true },
  sequenceName: { type: String, required: false },
});

const emit = defineEmits(["update:variant"]);
const isActiveLocal = ref(props.variant.is_active);
const showDeleteVariantModal = ref(false);
const lastKnownServerState = ref(props.variant.is_active);
const isUpdating = ref(false);

const hasSubject = computed(() => {
  return (
    props.stepType.includes("EMAIL") || props.stepType === "LINKEDIN_INMAIL"
  );
});

const isEmailType = computed(() => {
  return props.stepType.includes("EMAIL");
});

const isWhatsAppType = computed(() => {
  return props.stepType === sequenceStepTypes.WHATSAPP_MESSAGE;
});

const variantActions = [
  { id: 1, name: "Edit Variant", action: () => editVariant() },
  { id: 2, name: "Delete Variant", action: () => deleteVariant() },
];

const filteredVariantActions = computed(() => {
  let actions = variantActions;

  if (props.variantLength <= 1) {
    actions = actions.filter((action) => action.name !== "Delete Variant");
  }

  return actions;
});

const variantMetrics = computed(() => ({
  total_sent: props.variant.analytics?.total_sent || 0,
  total_opens: props.variant.analytics?.total_opens || 0,
  total_clicks: props.variant.analytics?.total_clicks || 0,
  total_replies: props.variant.analytics?.total_replies || 0,
}));

const editVariant = () => {
  if (!props.canEdit) return;
  router.push({
    path: `/sequences/${props.step.sequence_id}/step/${props.variant.id}/edit`,
  });
};

watch(isActiveLocal, (newValue, oldValue) => {
  if (newValue !== lastKnownServerState.value && !isUpdating.value) {
    updateVariantStatus();
  }
});

const deleteVariant = () => {
  showDeleteVariantModal.value = true;
};

const updateVariantStatus = async () => {
  if (isUpdating.value) return;

  if (isActiveLocal.value && isVariantEmpty(props.variant, props.step)) {
    message.warning('Please fill in all required fields before activating this variant.');
    isActiveLocal.value = lastKnownServerState.value;
    router.push(`/sequences/${props.step.sequence_id}/step/${props.variant.id}/edit`);
    return;
  }

  isUpdating.value = true;
  try {
    const response = await SequenceService.updateSequenceStepStatus(
      props.step.sequence_id,
      props.variant.id,
      isActiveLocal.value,
    );
    if (response.success) {
      message.success("Variant updated successfully");
      lastKnownServerState.value = isActiveLocal.value;
      // No need to emit update for simple status changes to avoid screen refresh
    } else {
      message.error(response.message || "Failed to update variant");
      isActiveLocal.value = lastKnownServerState.value;
    }
  } catch (error) {
    console.error("Error updating variant:", error);
    if (error.response?.status === 422 && error.response?.data?.message) {
      message.error(error.response.data.message);
    } else {
      message.error("Failed to update variant");
    }
    isActiveLocal.value = lastKnownServerState.value;
  } finally {
    setTimeout(() => {
      isUpdating.value = false;
    }, 200);
  }
};

const deleteVariantConfirm = async () => {
  try {
    const response = await SequenceService.deleteSequenceStepVariant(
      props.step.sequence_id,
      props.variant.id,
    );
    if (response.success) {
      message.success("Variant deleted successfully");
      emit("update:variant");
    } else {
      message.error(response.message || "Failed to delete variant");
    }
  } catch (error) {
    console.error("Error deleting variant:", error);
    if (error.response?.status === 422 && error.response?.data?.message) {
      message.error(error.response.data.message);
    } else {
      message.error("Failed to delete variant");
    }
  } finally {
    showDeleteVariantModal.value = false;
  }
};

const isTaskType = computed(() => {
  return (
    props.step.step_type === sequenceStepTypes.TASK ||
    props.step.step_type === sequenceStepTypes.PHONE_CALL ||
    props.step.step_type === sequenceStepTypes.MARKET_RESEARCH ||
    props.step.step_type === sequenceStepTypes.MULTI_THREADING
  );
});

const hasAIVariables = computed(() => {
  const message = props.variant.message || props.variant.instruction;
  const subject = props.variant.subject;
  return (message && message.includes('{{ai_variable.')) || (subject && subject.includes('{{ai_variable.'));
});

const fetchAIVariable = async (id) => {
  try {
    const response = await SequenceService.fetchAIVariable(id);
    if (response && response.id) {
      return response;
    }
    return { id, name: `Variable ${id}`, prompt: '', notFound: true };
  } catch (error) {
    console.error(`Error fetching AI variable ${id}:`, error);
    return { id, name: `Variable ${id}`, prompt: '', notFound: true };
  }
};

const getVariableName = (id) => {
  const variable = aiVariables.value.find(v => v.id == id);
  return variable ? variable.name : `Variable ${id}`;
};

const processedMessage = ref('');

const processMessageContent = async (message) => {
  return processAiVariableContent(message, getVariableName);
};

const computedMessage = computed(() => {
  const content = processedMessage.value || props.variant.message || props.variant.instruction;
  if (content) return content;
  if (props.step.step_type === sequenceStepTypes.QUALIFICATION) {
    return props.step.instruction || '';
  }
  return '';
});



const initializeVariantContent = async () => {
  const message = props.variant.message || props.variant.instruction;
  const subject = props.variant.subject;
  
  if (isWhatsAppType.value && message) {
    if (message) {
      processedMessage.value = message;
    }
    return;
  }
  
  if (hasAIVariables.value) {
    isProcessingVariables.value = true;
  }
  
  const content = [message, subject].filter(Boolean);
  const variableIds = extractAiVariableIds(content);
  
  if (variableIds.size > 0) {
    const fetchPromises = Array.from(variableIds).map(id => fetchAIVariable(id));
    const variables = await Promise.all(fetchPromises);
    aiVariables.value = variables.filter(v => v && v.id);
    
    if (message) {
      processedMessage.value = await processMessageContent(message);
    }
  } else {
    processedMessage.value = message;
  }
  
  isProcessingVariables.value = false;
};

onMounted(async () => {
  await initializeVariantContent();
});
</script>

<style scoped>
.step_separator {
  border-bottom: 1px solid var(--white-opacity-8);
}

.variant_letter {
  border-radius: 8px;
  border: 0.8px solid var(--white-opacity-8);
  background: var(--white-opacity-8);
}

:deep(ai-var.ai_variable_button) {
  color: var(--text-color);
  background: var(--white-opacity-14);
  border: 1px solid var(--white-opacity-8);
  border-radius: 16px;
  display: inline-block;
  user-select: none;
  font-size: 13px;
  line-height: 16px;
  vertical-align: middle;
}

:deep(ai-var.ai_variable_button):hover {
  background: var(--white-opacity-20);
}
</style>
